import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { rpc } from "./rpc";
import type { PublicShare } from "./shares";
import type { User } from "./types";

/**
 * Managing shares (`docs/spec/shares.md` §4). The anonymous half of the surface
 * lives in `shares.ts`, which is called without credentials; everything here is
 * a signed-in caller administering their own links.
 */
export interface Share extends PublicShare {
  createdBy?: User;
  /** Workspace administrators only; absent from every other response. */
  lastAccessedAt?: string | null;
}

export interface ShareTarget {
  documentId?: string;
  collectionId?: string;
}

export const sharingKeys = {
  forTarget: (target: ShareTarget) =>
    ["shares.info", target.documentId ?? "", target.collectionId ?? ""] as const,
  list: () => ["shares.list"] as const,
};

export interface ShareInfoResult {
  /** The share asked for, plus any ancestor share that also covers the target. */
  shares: Share[];
}

export function useShareInfoFor(
  target: ShareTarget,
  enabled: boolean,
): UseQueryResult<ShareInfoResult> {
  const id = target.documentId ?? target.collectionId ?? "";
  return useQuery({
    queryKey: sharingKeys.forTarget(target),
    enabled: enabled && id !== "",
    retry: false,
    queryFn: async () => {
      const result = await rpc<ShareInfoResult>("shares.info", target);
      return { shares: result.data.shares };
    },
  });
}

export function useShares(enabled = true): UseQueryResult<Share[]> {
  return useQuery({
    queryKey: sharingKeys.list(),
    enabled,
    retry: false,
    queryFn: async () => (await rpc<Share[]>("shares.list", { limit: 100 })).data,
  });
}

function useShareMutation<TInput extends ShareTarget>(
  method: string,
): UseMutationResult<Share, Error, TInput> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: TInput) => {
      const body = { ...input };
      delete body.documentId;
      delete body.collectionId;
      const sent = method === "shares.create" ? input : body;
      return (await rpc<Share>(method, sent)).data;
    },
    onSuccess: (_share, input) => {
      void client.invalidateQueries({ queryKey: sharingKeys.forTarget(input) });
      void client.invalidateQueries({ queryKey: sharingKeys.list() });
    },
  });
}

export interface ShareSettings {
  published?: boolean;
  includeChildDocuments?: boolean;
  allowIndexing?: boolean;
  showLastUpdated?: boolean;
  showTOC?: boolean;
  urlId?: string | null;
}

export type CreateShareInput = ShareTarget & ShareSettings;
export type UpdateShareInput = ShareTarget & ShareSettings & { id: string };
export type RevokeShareInput = ShareTarget & { id: string };

export const useCreateShare = (): UseMutationResult<Share, Error, CreateShareInput> =>
  useShareMutation<CreateShareInput>("shares.create");

export const useUpdateShare = (): UseMutationResult<Share, Error, UpdateShareInput> =>
  useShareMutation<UpdateShareInput>("shares.update");

export const useRevokeShare = (): UseMutationResult<Share, Error, RevokeShareInput> =>
  useShareMutation<RevokeShareInput>("shares.revoke");

/* ------------------------------------------------------------- the slug */

/** Routes a slug may never shadow (§2.3). */
export const RESERVED_URL_IDS: readonly string[] = [
  "api",
  "auth",
  "s",
  "doc",
  "collection",
  "search",
  "home",
  "settings",
  "static",
  "files",
  "robots.txt",
  "sitemap.xml",
];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The custom slug rules of §2.3, checked here so a bad one is refused before it
 * costs a round trip. The server checks them again, and owns uniqueness.
 */
export function validateUrlId(value: string): string | null {
  const slug = value.trim().toLowerCase();
  if (slug === "") return null;
  if (UUID.test(slug)) return "A link cannot look like an id.";
  if (slug.length < 4 || slug.length > 32) return "A link must be 4 to 32 characters.";
  if (!/^[a-z0-9-]+$/.test(slug)) return "Use lower-case letters, digits and hyphens only.";
  if (slug.startsWith("-") || slug.endsWith("-"))
    return "A link cannot start or end with a hyphen.";
  if (slug.includes("--")) return "A link cannot contain two hyphens in a row.";
  if (RESERVED_URL_IDS.includes(slug)) return "That link is reserved.";
  return null;
}
